import React, { useState, useEffect, useCallback } from 'react';
import { statusMeta, inr } from './constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  'new', 'contacted', 'nurturing', 'demo_scheduled', 'demo_done',
  'negotiating', 'closed_won', 'closed_lost', 'upgrade_target',
];

// Ready-made quick replies for the CRM live-chat (click → fills the reply box, edit & send).
const QUICK_REPLIES = [
  { label: '👋 स्वागत', text: 'नमस्ते! 🙏 शिक्षा मित्र में रुचि के लिए धन्यवाद। बताइए, आपके स्कूल में कुल कितने छात्र हैं?' },
  { label: '💰 Pricing', text: 'हमारा software 100% FREE है — सिर्फ़ एक बार का license/setup + छोटा सालाना maintenance। कोई per-student monthly charge नहीं। पूरी pricing: https://shiksha-mitra.com/pricing' },
  { label: '🎬 Demo link', text: 'मैं आपके लिए एक 30-मिनट की Live Demo book कर देता हूँ — इस link से अपनी सुविधा का समय चुन लीजिए: https://shiksha-mitra.com/book-demo' },
  { label: '📞 Call करें?', text: 'क्या हम आपको call कर सकते हैं? कृपया अपना सुविधाजनक समय बता दीजिए, हमारी team आपसे बात कर लेगी। 🙏' },
  { label: '⚙️ Setup समय', text: 'Demo के बाद पूरा setup सिर्फ़ 24-48 घंटे में हो जाता है — आपका अपना domain, WhatsApp और Gemini API connect करके। कोई तकनीकी झंझट नहीं।' },
  { label: '🤝 मदद', text: 'आपके सभी सवालों का जवाब देने के लिए मैं यहाँ हूँ — बेझिझक पूछिए। 🙏' },
  { label: '✅ धन्यवाद', text: 'धन्यवाद! 🙏 कोई और सवाल हो तो बताइएगा — हमारी team हमेशा आपके साथ है।' },
  { label: '🔄 सोचकर बताएँ', text: 'कोई बात नहीं, आराम से सोचिए। 🙏 जब भी तैयार हों, यहीं message कर दीजिए — मैं तुरंत मदद करूँगा।' },
];

const ACTIVITY_ICON = {
  lead_created: '✨', status_changed: '🔀', note_added: '📝', ai_chat: '🤖',
  bot_message: '💬', hot_alert: '🔥', call_logged: '📞', whatsapp_logged: '💬',
  meeting_logged: '🤝', email_logged: '✉️', voice_call: '📞', sim_call: '🎙️',
};

const recordingUrl = (a) => {
  const ref = a?.meta?.recording_ref;
  if (!ref) return '';
  if (a.type === 'sim_call') return `${API_URL}/api/voice-simulator/recording/${ref}`;
  if (a.type === 'voice_call') return `${API_URL}/api/live-chat/recording/${ref}`;
  return '';
};

// Legacy leads stored raw quick-reply ids ("selected: wa_ans_0"). Show something
// human instead until the readable-title fix backfills naturally.
const prettyText = (v) => {
  if (!v) return v;
  if (/^selected:\s*wa_ans_\d+/i.test(v.trim())) return '(विकल्प चुना)';
  return v;
};

// One chat bubble in the Aayush ↔ Lead conversation. Aayush (assistant) sits on
// the left, the lead (user) on the right — like a real WhatsApp thread. Diksha
// (demo assistant) messages reuse the assistant side but get their own label/colour.
const ChatBubble = ({ msg }) => {
  const isLead = msg.role === 'user';
  const isDiksha = msg.source === 'diksha';
  const isAgent = msg.source === 'human_agent';
  const time = msg.timestamp ? new Date(msg.timestamp).toLocaleString('hi-IN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  const text = (msg.content || '').trim() || '—';
  const label = isLead ? '🙋 Lead' : (isAgent ? `🧑‍💼 आप${msg.agent_name ? ' (' + msg.agent_name + ')' : ' (Team)'}` : (isDiksha ? '🎬 दीक्षा (Demo)' : '🤖 आयुष'));
  const assistantStyle = isAgent ? 'bg-emerald-600 text-white rounded-bl-sm' : (isDiksha ? 'bg-violet-50 border border-violet-200 text-slate-700 rounded-bl-sm' : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm');
  return (
    <div className={`flex ${isLead ? 'justify-end' : 'justify-start'}`} data-testid={`crm-chat-bubble-${isLead ? 'user' : (isAgent ? 'agent' : (isDiksha ? 'diksha' : 'assistant'))}`}>
      <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
        isLead ? 'bg-[#dcf8c6] text-slate-800 rounded-br-sm' : assistantStyle
      }`}>
        <p className={`text-[10px] font-semibold mb-0.5 ${isLead ? 'text-green-700' : (isAgent ? 'text-emerald-100' : (isDiksha ? 'text-violet-600' : 'text-indigo-500'))}`}>
          {label}
        </p>
        {text}
        {time && <p className={`text-[9px] mt-1 text-right ${isAgent ? 'text-emerald-100' : 'text-slate-400'}`}>{time}</p>}
      </div>
    </div>
  );
};

const TimelineItem = ({ a }) => (
  <div className="flex gap-3" data-testid={`crm-activity-${a.id}`}>
    <div className="flex flex-col items-center">
      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm shrink-0">
        {ACTIVITY_ICON[a.type] || '•'}
      </div>
      <div className="w-px flex-1 bg-slate-200 my-1" />
    </div>
    <div className="pb-4 flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-800">{a.title}</p>
      {a.detail && <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap break-words">{prettyText(a.detail)}</p>}
      {recordingUrl(a) && (
        <audio controls preload="none" className="mt-2 h-9 w-full max-w-xs"
          src={recordingUrl(a)} data-testid={`crm-timeline-recording-${a.id}`} />
      )}
      <p className="text-[11px] text-slate-400 mt-1">
        {a.actor && a.actor !== 'system' ? `${a.actor} · ` : ''}
        {a.created_at ? new Date(a.created_at).toLocaleString('hi-IN') : ''}
      </p>
    </div>
  </div>
);

const JOURNEY_STEPS = [
  { key: 'demo', label: 'Demo', icon: '🎬' },
  { key: 'data', label: 'Data Upload', icon: '📂' },
  { key: 'payment_link', label: 'Payment Link', icon: '📲' },
  { key: 'subscribed', label: 'Subscribed', icon: '💳' },
];

const JourneyTracker = ({ journey }) => {
  const steps = journey.steps || {};
  return (
    <div className="rounded-xl p-3 border border-indigo-100 bg-indigo-50/50" data-testid="crm-journey-tracker">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-slate-700">🧭 Conversion Journey</p>
        <span className="text-[11px] font-semibold text-indigo-600" data-testid="crm-journey-progress">
          {journey.completed}/{journey.total} पूरे
        </span>
      </div>
      <div className="flex items-start">
        {JOURNEY_STEPS.map((s, i) => {
          const done = !!steps[s.key];
          const nextDone = i < JOURNEY_STEPS.length - 1 && !!steps[JOURNEY_STEPS[i + 1].key];
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center w-16 shrink-0" data-testid={`crm-journey-step-${s.key}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm transition ${
                  done ? 'bg-green-500 text-white shadow-sm' : 'bg-white border-2 border-slate-200 text-slate-300'}`}>
                  {done ? '✓' : s.icon}
                </div>
                <p className={`text-[10px] text-center mt-1 leading-tight ${done ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>{s.label}</p>
              </div>
              {i < JOURNEY_STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mt-4 rounded transition ${nextDone && done ? 'bg-green-400' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {journey.school_name && (
        <p className="text-[11px] text-slate-500 mt-2">🏫 {journey.school_name}{journey.account_state ? ` · ${journey.account_state}` : ''}</p>
      )}
    </div>
  );
};


export default function LeadDetailDrawer({ token, leadId, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [logType, setLogType] = useState('call');
  const [logDetail, setLogDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSession, setChatSession] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [journey, setJourney] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskType, setTaskType] = useState('followup');
  const [taskDue, setTaskDue] = useState('');
  const [editingScore, setEditingScore] = useState(false);
  const [scoreInput, setScoreInput] = useState(0);
  const [trial, setTrial] = useState(null);
  const [invStatus, setInvStatus] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [takeover, setTakeover] = useState(false);
  const [outsideWindow, setOutsideWindow] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchTasks = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${leadId}/tasks`, { headers });
      const d = await res.json();
      if (d.success) setTasks(d.tasks || []);
    } catch (e) { console.error('tasks', e); }
  }, [leadId, token]);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${leadId}`, { headers });
      const d = await res.json();
      if (d.success) { setLead(d.lead); setActivities(d.activities || []); setPayment(d.payment || null); setJourney(d.journey || null); }
      await fetchTasks();
      try {
        const tr = await fetch(`${API_URL}/api/crm/leads/${leadId}/trial-status`, { headers });
        const td = await tr.json();
        if (td.success) setTrial(td);
      } catch (e) { /* trial status optional */ }
    } catch (e) { console.error('detail', e); }
    finally { setLoading(false); }
  }, [leadId, token, fetchTasks]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // Load the FULL Aayush ↔ Lead conversation from ALL linked sessions (WhatsApp +
  // website), merged into one timeline. Polls every 15s so the admin sees new
  // inbound lead replies live while chatting.
  const loadChat = useCallback(async ({ silent = false } = {}) => {
    if (!lead) return;
    const digits = (lead.phone || '').replace(/[^0-9]/g, '');
    const candidates = [];
    if (digits) candidates.push(`wa-${digits}`);
    (lead.ai_session_ids || []).forEach((s) => s && candidates.push(s));
    if (lead.ai_session_id) candidates.push(lead.ai_session_id);
    const uniq = [...new Set(candidates)];
    if (!uniq.length) { setChat([]); return; }
    if (!silent) { setChatLoading(true); setSummary(null); }
    try {
      let all = [];
      let firstWithMsgs = null;
      for (const sid of uniq) {
        const res = await fetch(`${API_URL}/api/live-chat/chat-history/${encodeURIComponent(sid)}`);
        const d = await res.json();
        if (d.success && (d.messages || []).length) {
          all = all.concat(d.messages.map((m) => ({ ...m, _session: sid })));
          if (!firstWithMsgs) firstWithMsgs = sid;
        }
      }
      all.sort((a, b) => new Date(a.created_at || a.timestamp || 0) - new Date(b.created_at || b.timestamp || 0));
      setChat(all);
      setChatSession(firstWithMsgs || (digits ? `wa-${digits}` : null));
    } catch (e) { console.error('chat-history', e); }
    finally { if (!silent) setChatLoading(false); }
    // Refresh takeover state (whether Aayush is paused for this lead).
    if (digits) {
      try {
        const tr = await fetch(`${API_URL}/api/whatsapp/takeover-status?phone=${digits}`, { headers });
        const td = await tr.json();
        if (td.success) setTakeover(!!td.human_takeover);
      } catch (e) { /* optional */ }
    }
  }, [lead, token]);

  useEffect(() => {
    loadChat();
    const iv = setInterval(() => loadChat({ silent: true }), 15000);
    return () => clearInterval(iv);
  }, [loadChat]);

  const phoneDigitsForReply = (lead?.phone || '').replace(/[^0-9]/g, '');

  const sendReply = async () => {
    const msg = replyText.trim();
    if (!msg || phoneDigitsForReply.length < 10) return;
    setSendingReply(true); setOutsideWindow(false);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/reply-to-lead`, {
        method: 'POST', headers,
        body: JSON.stringify({ phone: phoneDigitsForReply, message: msg, lead_id: leadId }),
      });
      const d = await res.json();
      if (d.sent) {
        setReplyText(''); setTakeover(true);
        await loadChat({ silent: true });
      } else if (d.outside_window) {
        setOutsideWindow(true);
      } else {
        setInvStatus('❌ Reply नहीं भेजा जा सका: ' + (d.error || 'unknown'));
      }
    } catch (e) { setInvStatus('❌ नेटवर्क समस्या'); }
    finally { setSendingReply(false); }
  };

  const resumeBot = async () => {
    if (phoneDigitsForReply.length < 10) return;
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/whatsapp/resume-bot`, {
        method: 'POST', headers, body: JSON.stringify({ phone: phoneDigitsForReply }),
      });
      setTakeover(false);
    } catch (e) { /* ignore */ }
    finally { setBusy(false); }
  };


  const genSummary = async () => {
    if (!chatSession) return;
    setSummaryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/conversation-summary`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: chatSession }),
      });
      const d = await res.json();
      if (d.success) setSummary(d);
      else setSummary({ summary: d.error === 'ai_unavailable' ? 'AI अभी उपलब्ध नहीं है।' : 'बातचीत नहीं मिली।', next_step: '', interest: '' });
    } catch (e) { setSummary({ summary: 'सारांश बनाने में दिक्कत: ' + e.message, next_step: '', interest: '' }); }
    finally { setSummaryLoading(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/crm/leads/${leadId}/note`, {
        method: 'POST', headers, body: JSON.stringify({ text: noteText.trim() }) });
      setNoteText('');
      await fetchDetail();
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const logActivity = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/crm/leads/${leadId}/log-activity`, {
        method: 'POST', headers, body: JSON.stringify({ type: logType, detail: logDetail.trim() }) });
      setLogDetail('');
      await fetchDetail();
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/crm/leads/${leadId}/status`, {
        method: 'POST', headers, body: JSON.stringify({ status }) });
      await fetchDetail();
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const phoneDigits = (lead?.phone || '').replace(/[^0-9]/g, '');

  const sendDemoInvite = async () => {
    if (!phoneDigits || phoneDigits.length < 10) { setInvStatus('❌ सही WhatsApp नंबर नहीं मिला'); return; }
    setBusy(true); setInvStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/send-demo-invite`, {
        method: 'POST', headers,
        body: JSON.stringify({ phone: phoneDigits, name: lead?.name || '', lead_id: leadId }),
      });
      const d = await res.json();
      if (d.success && d.sent) setInvStatus(`✅ Demo booking link भेज दिया (${d.via === 'template' ? 'template' : 'text'})`);
      else if (d.success) setInvStatus('⚠️ भेजा नहीं जा सका — नंबर WhatsApp पर सक्रिय नहीं या template अभी approve नहीं हुआ');
      else setInvStatus('❌ ' + (d.detail || 'नहीं भेज पाया'));
      fetchDetail();
    } catch (e) { setInvStatus('❌ नेटवर्क समस्या'); }
    finally { setBusy(false); }
  };

  const saveManualScore = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/crm/leads/${leadId}/score`, {
        method: 'POST', headers, body: JSON.stringify({ score: Math.max(0, Math.min(100, Number(scoreInput) || 0)) }) });
      setEditingScore(false);
      await fetchDetail();
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const resetScore = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/crm/leads/${leadId}/score/reset`, { method: 'POST', headers });
      setEditingScore(false);
      await fetchDetail();
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const addTask = async () => {
    if (!taskTitle.trim() || !taskDue) { alert('Task title और due time दोनों डालें'); return; }
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/crm/leads/${leadId}/tasks`, {
        method: 'POST', headers,
        body: JSON.stringify({ title: taskTitle.trim(), type: taskType, due_at: new Date(taskDue).toISOString() }) });
      setTaskTitle(''); setTaskDue('');
      await fetchTasks();
    } finally { setBusy(false); }
  };

  const completeTask = async (taskId) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/crm/tasks/${taskId}/complete`, { method: 'POST', headers });
      await fetchTasks();
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const TASK_TYPES = [
    { v: 'call', l: '📞 Call' }, { v: 'whatsapp', l: '💬 WhatsApp' },
    { v: 'meeting', l: '🤝 Meeting' }, { v: 'followup', l: '🔔 Follow-up' },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="crm-lead-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-slate-50 h-full shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
        {loading || !lead ? (
          <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-500"></div></div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-10 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-slate-800 truncate">{lead.name || 'Lead'}</h3>
                  <p className="text-sm text-slate-500">{lead.phone || '—'}</p>
                  {lead.school_name && <p className="text-xs text-slate-400 mt-0.5">🏫 {lead.school_name}{lead.city ? ` · ${lead.city}` : ''}</p>}
                </div>
                <button onClick={onClose} data-testid="crm-drawer-close" className="text-slate-400 hover:text-slate-700 text-2xl leading-none">&times;</button>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <button onClick={() => { setScoreInput(lead.lead_score || 0); setEditingScore(v => !v); }}
                  data-testid="crm-drawer-score-edit"
                  title="Score manually बदलें"
                  className="px-2.5 py-1 rounded-full bg-slate-800 text-white text-xs font-bold inline-flex items-center gap-1 hover:bg-slate-700">
                  Score {lead.lead_score || 0} <span className="opacity-70">✏️</span>
                </button>
                {lead.score_locked && (
                  <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold border border-amber-200" data-testid="crm-drawer-score-locked" title={`Manually set${lead.score_set_by ? ' · ' + lead.score_set_by : ''}`}>🔒 Manual</span>
                )}
                <span className={`px-2.5 py-1 rounded-full text-white text-xs font-semibold ${lead.temperature === 'hot' ? 'bg-red-500' : lead.temperature === 'warm' ? 'bg-orange-400' : 'bg-slate-400'}`}>
                  {lead.temperature === 'hot' ? '🔥 Hot' : lead.temperature === 'warm' ? '🟡 Warm' : '🔵 Cold'}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">Deal {inr(lead.deal_value)}</span>
              </div>
              {/* Manual score override */}
              {editingScore && (
                <div className="mt-3 p-3 rounded-lg bg-slate-100 border border-slate-200" data-testid="crm-drawer-score-editor">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[11px] text-slate-500 uppercase font-medium">Manual Score Override (0–100)</label>
                    <span className="text-sm font-bold text-slate-800 tabular-nums">{scoreInput}</span>
                  </div>
                  <input type="range" min="0" max="100" value={scoreInput}
                    onChange={(e) => setScoreInput(Number(e.target.value))}
                    data-testid="crm-drawer-score-slider" className="w-full accent-slate-800" />
                  <div className="flex items-center gap-2 mt-2">
                    <input type="number" min="0" max="100" value={scoreInput}
                      onChange={(e) => setScoreInput(Number(e.target.value))}
                      data-testid="crm-drawer-score-input"
                      className="w-20 px-2 py-1.5 rounded-md border border-slate-300 text-sm" />
                    <button onClick={saveManualScore} disabled={busy} data-testid="crm-drawer-score-save"
                      className="px-3 py-1.5 rounded-md bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-50">Set & Lock</button>
                    <button onClick={resetScore} disabled={busy} data-testid="crm-drawer-score-reset"
                      className="px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-white disabled:opacity-50">Auto पर reset</button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">Manual score set करने पर auto-sync इसे overwrite नहीं करेगा (lock रहेगा)।</p>
                </div>
              )}
              {/* Status changer */}
              <div className="mt-3">
                <label className="text-[11px] text-slate-500 uppercase">Status</label>
                <select value={lead.status} disabled={busy} onChange={(e) => changeStatus(e.target.value)}
                  data-testid="crm-drawer-status"
                  className={`mt-1 w-full px-3 py-2 rounded-lg text-sm font-medium border-0 cursor-pointer ${statusMeta(lead.status).color}`}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{statusMeta(s).label}</option>)}
                </select>
              </div>
              {/* Scheduled Demo: date/time + meeting link */}
              {lead.demo_scheduled_label && (
                <div className="mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200" data-testid="crm-drawer-demo-info">
                  <p className="text-[11px] font-semibold text-orange-700 uppercase mb-1.5">🎬 Scheduled Demo</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">समय</span>
                    <span className="font-semibold text-orange-700" data-testid="crm-drawer-demo-time">{lead.demo_scheduled_label} IST</span>
                  </div>
                  {lead.demo_meet_link && (
                    <a href={lead.demo_meet_link} target="_blank" rel="noopener noreferrer"
                      data-testid="crm-drawer-demo-meet-link"
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 transition">
                      🔗 Meeting link खोलें
                    </a>
                  )}
                </div>
              )}
              {/* Trial status: did the user RECEIVE the credentials & LOG IN? */}
              {trial && (trial.credentials_sent || trial.first_login_done) && (
                <div className="mt-3 p-3 rounded-lg bg-indigo-50 border border-indigo-200" data-testid="crm-drawer-trial-status">
                  <p className="text-[11px] font-semibold text-indigo-700 uppercase mb-2">🎫 Trial Status</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Credentials</span>
                    <span className="font-semibold" data-testid="crm-trial-delivery">
                      {trial.delivery_status === 'read' ? '👁 पढ़ा गया'
                        : trial.delivery_status === 'delivered' ? '✓✓ Delivered'
                        : trial.delivery_status === 'failed' ? '⚠️ Failed'
                        : trial.credentials_sent ? '✓ भेजा गया' : '— नहीं भेजा'}
                    </span>
                  </div>
                  {trial.login_id && <div className="flex items-center justify-between text-xs mt-1 text-slate-500"><span>Login ID</span><span className="font-mono">{trial.login_id}</span></div>}
                  {(trial.read_at || trial.delivered_at || trial.credentials_sent_at) && (
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {trial.read_at ? `पढ़ा: ${new Date(trial.read_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`
                        : trial.delivered_at ? `Delivered: ${new Date(trial.delivered_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`
                        : `भेजा: ${new Date(trial.credentials_sent_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-indigo-100">
                    <span className="text-slate-600">First Login</span>
                    <span className={`font-semibold ${trial.first_login_done ? 'text-emerald-600' : 'text-slate-400'}`} data-testid="crm-trial-first-login">
                      {trial.first_login_done ? '✅ हो गया' : '❌ अभी नहीं'}
                    </span>
                  </div>
                  {trial.first_login_at && <div className="text-[11px] text-slate-400 mt-0.5">{new Date(trial.first_login_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>}
                </div>
              )}
              {/* Quick contact actions */}
              <div className="flex gap-2 mt-3">
                <a href={`tel:+${phoneDigits}`} data-testid="crm-drawer-call"
                  className="flex-1 text-center px-3 py-2 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600">📞 Call</a>
                <a href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noopener noreferrer" data-testid="crm-drawer-wa"
                  className="flex-1 text-center px-3 py-2 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600">💬 WhatsApp</a>
              </div>
              <button onClick={sendDemoInvite} disabled={busy} data-testid="crm-drawer-send-demo-invite"
                className="w-full mt-2 px-3 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
                📅 Demo Booking link भेजें
              </button>
              {invStatus && <p className="text-[11px] mt-1 text-slate-600" data-testid="crm-drawer-invite-status">{invStatus}</p>}
            </div>

            <div className="p-4 space-y-4">
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { l: 'Email', v: lead.email || '—' },
                  { l: 'पद/Role', v: lead.role || '—' },
                  { l: 'छात्र', v: lead.student_count || '—' },
                  { l: 'शिक्षक', v: lead.teacher_count || '—' },
                  { l: 'Source', v: (lead.sources || [lead.source]).join(', ') },
                  { l: 'Last intent', v: prettyText(lead.last_intent) || '—' },
                ].map((it, i) => (
                  <div key={i} className="bg-white rounded-lg p-2.5 border border-slate-100">
                    <p className="text-[10px] text-slate-400 uppercase">{it.l}</p>
                    <p className="text-sm text-slate-700 truncate">{it.v}</p>
                  </div>
                ))}
              </div>

              {/* Conversion Journey tracker */}
              {journey && <JourneyTracker journey={journey} />}

              {/* Activation & Payment detail */}
              {payment && (
                <div className={`rounded-xl p-3 border ${payment.paid ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid="crm-payment-card">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-slate-700">💳 Activation & Payment</p>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${payment.paid ? 'bg-green-600 text-white' : 'bg-amber-500 text-white'}`} data-testid="crm-payment-status">
                      {payment.paid ? '✅ Paid' : '⏳ Pending'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Plan</p>
                      <p className="text-sm text-slate-700">{payment.plan}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Amount</p>
                      <p className="text-sm font-semibold text-slate-800">{payment.paid ? inr(payment.amount) : '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">School Active</p>
                      <p className="text-sm text-slate-700">{payment.school_activated ? '✅ हाँ' : '❌ नहीं'}</p>
                    </div>
                    {payment.school_name && (
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">School</p>
                        <p className="text-sm text-slate-700 truncate">{payment.school_name}</p>
                      </div>
                    )}
                  </div>
                  {!payment.paid && (
                    <p className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2" data-testid="crm-activation-note">
                      ℹ️ Lead को login (Demo credentials) मिलने के बाद वो खुद portal में "Activate My Trial" से अपना 14-दिन trial शुरू करता है। अलग activation link भेजने की ज़रूरत नहीं।
                    </p>
                  )}
                </div>
              )}

              {/* Tasks & Reminders */}
              <div className="bg-white rounded-xl p-3 border border-slate-200" data-testid="crm-tasks-card">
                <p className="text-sm font-bold text-slate-700 mb-2">📋 Tasks & Reminders</p>
                <div className="space-y-2">
                  {tasks.filter(t => t.status === 'open').length === 0 && (
                    <p className="text-xs text-slate-400" data-testid="crm-tasks-empty">कोई pending task नहीं।</p>
                  )}
                  {tasks.filter(t => t.status === 'open').map((t) => (
                    <div key={t.id} className={`flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 border ${t.is_overdue ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`crm-task-${t.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm text-slate-700 truncate">{t.type_label} · {t.title}</p>
                        <p className={`text-[10px] ${t.is_overdue ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                          {t.is_overdue ? '⚠️ Overdue · ' : ''}{t.due_at ? new Date(t.due_at).toLocaleString('hi-IN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                      <button onClick={() => completeTask(t.id)} disabled={busy} data-testid={`crm-task-complete-${t.id}`}
                        className="shrink-0 text-[11px] px-2 py-1 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">✓ Done</button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2">
                  <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Task (जैसे: Principal को call करें)" data-testid="crm-task-title-input"
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-orange-400 outline-none" />
                  <div className="flex gap-2">
                    <select value={taskType} onChange={(e) => setTaskType(e.target.value)} data-testid="crm-task-type-select"
                      className="px-2 py-1.5 text-sm border border-slate-200 rounded-lg outline-none">
                      {TASK_TYPES.map(tt => <option key={tt.v} value={tt.v}>{tt.l}</option>)}
                    </select>
                    <input type="datetime-local" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} data-testid="crm-task-due-input"
                      className="flex-1 px-2 py-1.5 text-sm border border-slate-200 rounded-lg outline-none" />
                  </div>
                  <button onClick={addTask} disabled={busy} data-testid="crm-task-add-btn"
                    className="w-full px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 disabled:opacity-50">
                    + Task/Reminder जोड़ें
                  </button>
                </div>
              </div>

              {/* Aayush ↔ Lead full conversation — readable thread */}
              <div className="bg-white rounded-xl p-3 border border-slate-100" data-testid="crm-conversation-card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    💬 पूरी बातचीत <span className="text-[11px] font-normal text-slate-400">(आयुष ↔ Lead · दीक्षा Demo)</span>
                  </p>
                  {chat.length > 0 && (
                    <button onClick={genSummary} disabled={summaryLoading} data-testid="crm-ai-summary-btn"
                      className="px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 text-white text-xs font-semibold hover:from-indigo-600 hover:to-violet-700 disabled:opacity-60 flex items-center gap-1.5 shadow-sm">
                      {summaryLoading ? <><span className="animate-spin inline-block h-3 w-3 border-b-2 border-white rounded-full" />बना रहा हूँ…</> : <>✨ AI सारांश</>}
                    </button>
                  )}
                </div>

                {summary && (
                  <div className="mb-3 rounded-lg border border-indigo-100 bg-indigo-50/70 p-3 space-y-2" data-testid="crm-ai-summary-box">
                    <div>
                      <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-wide">सारांश</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{summary.summary}</p>
                    </div>
                    {summary.next_step && (
                      <div>
                        <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide">🎯 अगला कदम</p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{summary.next_step}</p>
                      </div>
                    )}
                    {summary.interest && (
                      <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-indigo-600">रुचि: {summary.interest}</span>
                    )}
                  </div>
                )}

                {chatLoading ? (
                  <p className="text-xs text-slate-400 text-center py-4">बातचीत लोड हो रही है…</p>
                ) : chat.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">अभी तक कोई आयुष बातचीत record नहीं हुई</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1 bg-slate-50/60 rounded-lg p-2" data-testid="crm-conversation-thread">
                    {chat.map((m, i) => {
                      const showDikshaDivider = m.source === 'diksha' && (i === 0 || chat[i - 1]?.source !== 'diksha');
                      return (
                        <React.Fragment key={i}>
                          {showDikshaDivider && (
                            <div className="flex items-center gap-2 my-2" data-testid="crm-diksha-divider">
                              <div className="flex-1 h-px bg-violet-200" />
                              <span className="text-[10px] font-semibold text-violet-500 px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200">🎬 दीक्षा Demo शुरू</span>
                              <div className="flex-1 h-px bg-violet-200" />
                            </div>
                          )}
                          <ChatBubble msg={m} />
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

                {/* 🧑‍💼 Direct reply to lead (WhatsApp, 24h window) + human takeover */}
                <div className="mt-3 pt-3 border-t border-slate-100" data-testid="crm-live-reply">
                  {takeover && (
                    <div className="flex items-center justify-between gap-2 mb-2 px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-200" data-testid="crm-takeover-banner">
                      <span className="text-[11px] text-amber-700">⏸️ आयुष रुका है — आप खुद chat कर रहे हैं</span>
                      <button onClick={resumeBot} disabled={busy} data-testid="crm-resume-bot-btn"
                        className="text-[11px] px-2 py-1 rounded-md bg-amber-500 text-white hover:bg-amber-600 whitespace-nowrap">▶️ Aayush चालू करें</button>
                    </div>
                  )}
                  {outsideWindow ? (
                    <div className="rounded-lg bg-rose-50 border border-rose-200 p-2.5" data-testid="crm-outside-window">
                      <p className="text-[11px] text-rose-700 mb-2">⚠️ 24-घंटे की WhatsApp window बंद है (lead ने 24 घंटे में reply नहीं किया)। अब सीधा message नहीं जा सकता — approved template भेजें।</p>
                      <button onClick={() => { sendDemoInvite(); setOutsideWindow(false); }} disabled={busy}
                        data-testid="crm-outside-window-invite-btn"
                        className="w-full px-3 py-2 rounded-lg bg-orange-500 text-white text-xs font-medium hover:bg-orange-600">📅 Demo Booking link (template) भेजें</button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1.5" data-testid="crm-quick-replies">
                        {QUICK_REPLIES.map((q) => (
                          <button key={q.label} type="button" onClick={() => setReplyText(q.text)}
                            data-testid={`crm-quick-reply-${q.label}`}
                            className="text-[11px] px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors whitespace-nowrap">
                            {q.label}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-end gap-2">
                        <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                          rows={2} placeholder="Lead को सीधा WhatsApp message लिखें…"
                          data-testid="crm-reply-input"
                          className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:ring-2 focus:ring-green-400 outline-none" />
                        <button onClick={sendReply} disabled={sendingReply || !replyText.trim()} data-testid="crm-reply-send-btn"
                          className="px-3 py-2 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600 disabled:opacity-40 whitespace-nowrap">
                          {sendingReply ? '…' : '➤ भेजें'}
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1">पहला message भेजते ही आयुष अपने-आप रुक जाएगा। Enter = भेजें, Shift+Enter = नई line।</p>
                </div>
              </div>

              {/* Log activity */}
              <div className="bg-white rounded-xl p-3 border border-slate-100">
                <p className="text-xs font-semibold text-slate-600 mb-2">⚡ Activity Log करें</p>
                <div className="flex gap-2 mb-2">
                  {[['call', '📞 Call'], ['whatsapp', '💬 WA'], ['meeting', '🤝 Meeting'], ['email', '✉️ Email']].map(([v, lbl]) => (
                    <button key={v} onClick={() => setLogType(v)} data-testid={`crm-log-type-${v}`}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium ${logType === v ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>{lbl}</button>
                  ))}
                </div>
                <textarea value={logDetail} onChange={(e) => setLogDetail(e.target.value)} rows={2}
                  data-testid="crm-log-detail"
                  placeholder="क्या बात हुई? (जैसे: founding offer में interested, कल call करेंगे)"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-slate-200" />
                <button onClick={logActivity} disabled={busy} data-testid="crm-log-submit"
                  className="mt-2 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
                  Log करें
                </button>
              </div>

              {/* Add note */}
              <div className="bg-white rounded-xl p-3 border border-slate-100">
                <p className="text-xs font-semibold text-slate-600 mb-2">📝 Note जोड़ें</p>
                <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
                  data-testid="crm-note-input"
                  placeholder="कोई internal note..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-slate-200" />
                <button onClick={addNote} disabled={busy || !noteText.trim()} data-testid="crm-note-submit"
                  className="mt-2 px-3 py-1.5 rounded-lg bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 disabled:opacity-50">
                  Note Save करें
                </button>
              </div>

              {/* Timeline */}
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <p className="text-sm font-bold text-slate-700 mb-3">📜 Activity Timeline ({activities.length})</p>
                {activities.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">कोई activity नहीं</p>
                ) : (
                  <div data-testid="crm-timeline">
                    {activities.map(a => <TimelineItem key={a.id} a={a} />)}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
